/**
 * src/components/agent/AgentShell.styles.js — Extracted styles for AgentShell.
 *
 * Phase 82-03 — Chat System Unification.
 *
 * Design tokens only (src/styles.js). No raw hex — see docs/CLAUDE_DESIGN_KIT.md
 * and the HARD DEPLOYMENT RULES ("Color & Token Changes") in CLAUDE.md.
 */

const SIDEBAR_WIDTH_DESKTOP = 280;
const SIDEBAR_WIDTH_TABLET = 240;

export function getShellRootStyle(tier, mode) {
  const base = {
    display: 'grid',
    width: '100%',
    height: '100%',
    minHeight: 0,
    background: 'var(--bg, var(--cream))',
    color: 'var(--navy)',
    position: 'relative',
    overflow: 'hidden',
  };

  if (mode === 'modal') {
    return {
      ...base,
      position: 'fixed',
      inset: 0,
      zIndex: 1100,
      background: 'var(--bg, var(--cream))',
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto 1fr auto',
    };
  }

  // 'panel': embedded pane (Command Center split view) — single column on every tier;
  // the host sizes it. Same rows as the mobile page layout below.
  if (mode === 'panel') {
    return {
      ...base,
      // Sits inside a --card pane beside --card surfaces: same fill, no grey slab.
      background: 'var(--card)',
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto 1fr auto',
    };
  }

  if (tier === 'desktop') {
    return {
      ...base,
      gridTemplateColumns: `${SIDEBAR_WIDTH_DESKTOP}px 1fr`,
      gridTemplateRows: 'auto 1fr auto',
    };
  }

  if (tier === 'tablet') {
    return {
      ...base,
      gridTemplateColumns: `${SIDEBAR_WIDTH_TABLET}px 1fr`,
      gridTemplateRows: 'auto 1fr auto',
    };
  }

  // mobile page mode (full-screen in view)
  return {
    ...base,
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto 1fr auto',
  };
}

export const sidebarAreaStyle = {
  gridRow: '1 / 4',
  gridColumn: '1 / 2',
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
  borderRight: '1px solid var(--bdr)',
  background: 'var(--glass)',
  display: 'flex',
  flexDirection: 'column',
};

export const headerAreaStyle = {
  gridRow: '1 / 2',
  // iOS safe-area sweep: top inset baked into the exported style object (not
  // only the AgentShell JSX inline override) so any future consumer of
  // headerAreaStyle still clears the Dynamic Island. minWidth:0 lets the grid
  // cell shrink so a long title/chip row can't force horizontal overflow.
  padding: 'max(8px, env(safe-area-inset-top)) 16px 8px',
  minWidth: 0,
  borderBottom: '1px solid var(--bdr)',
  background: 'var(--bg, var(--cream))',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 52,
};

export const bodyAreaStyle = {
  gridRow: '2 / 3',
  minHeight: 0,
  // iOS safe-area sweep — right-side truncation fix: a grid `1fr` track won't
  // shrink below its content's min-size without minWidth:0, so wide content
  // (a long word, code, the tool-chip row) overflowed right and was clipped by
  // the root's overflow:hidden. paddingInline also gives bubbles breathing room
  // and clears the landscape notch (safe-area-inset-left/right).
  minWidth: 0,
  paddingInline: 'max(12px, env(safe-area-inset-left)) max(12px, env(safe-area-inset-right))',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg, var(--cream))',
};

export const composerAreaStyle = {
  gridRow: '3 / 4',
  minWidth: 0,
  borderTop: '1px solid var(--bdr)',
  padding: '8px 12px calc(8px + env(safe-area-inset-bottom))',
  background: 'var(--glass)',
};

export const mobileDrawerBackdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'var(--scrim)',
  zIndex: 1150,
};

export const mobileDrawerPanelStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  // iOS safe-area sweep — the chat-list drawer is fixed at top:0/left:0, so its
  // "New chat" button + list header rendered UNDER the Dynamic Island / status
  // bar (CEO 2026-06-10: "can't create a new chat, it's under the island").
  // Pad by the top + left insets; the glass bg fills the inset area. The fix
  // lives on the drawer panel (not ChatSidebarV2 headerStyle) so the desktop
  // inline sidebar isn't double-padded.
  paddingTop: 'env(safe-area-inset-top, 0px)',
  paddingLeft: 'env(safe-area-inset-left, 0px)',
  width: 'min(86vw, 320px)',
  background: 'var(--glass)',
  borderRight: '1px solid var(--bdr)',
  zIndex: 1160,
  display: 'flex',
  flexDirection: 'column',
};

export const hamburgerBtnStyle = {
  minWidth: 44,
  minHeight: 44,
  width: 44,
  height: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  color: 'var(--navy)',
  cursor: 'pointer',
  borderRadius: 8,
  padding: 0,
};

export const closeBtnStyle = {
  minWidth: 44,
  minHeight: 44,
  width: 44,
  height: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  color: 'var(--navy)',
  cursor: 'pointer',
  borderRadius: 8,
  padding: 0,
  marginLeft: 'auto',
};

export const titleStyle = {
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--navy)',
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const placeholderStyle = {
  padding: '16px',
  color: 'var(--tm)',
  fontSize: 13,
  textAlign: 'center',
};
